"use client";

import { useEffect, useRef } from "react";
import { captureClientEvent } from "@/lib/analytics/client";
import type {
  AnalyticsEventName,
  AnalyticsProperties,
} from "@/lib/analytics/events";

/**
 * Fires a screen-view event once per mounted instance of a screen.
 *
 * Two things make this non-trivial in this app, and both are why the naive
 * `useEffect(() => capture(...), [])` is wrong here:
 *
 * - Next 16 keeps the route you navigated away from mounted (`display: none`),
 *   so a component can stay alive across navigations. Keying on the identity
 *   argument means returning to a *different* entity re-reports, while a
 *   re-render of the same one does not.
 * - React Strict Mode double-invokes effects in development, which would
 *   double-count every view.
 */
export function useScreenView(
  event: AnalyticsEventName,
  identity: string | number | null | undefined,
  properties?: AnalyticsProperties,
) {
  const reportedRef = useRef<string | null>(null);
  // Properties are read at fire time so a caller can pass an inline object
  // without the effect re-running on every render.
  const propertiesRef = useRef(properties);
  propertiesRef.current = properties;

  useEffect(() => {
    if (identity == null) return;

    const key = `${event}:${identity}`;
    if (reportedRef.current === key) return;
    reportedRef.current = key;

    captureClientEvent(event, propertiesRef.current);
  }, [event, identity]);
}
